import {
  Checkbox,
  Icon,
  NewTable,
  Section,
  useToast,
} from '@vallourec-ui/vallourec-ui'
import React, { useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { isAxiosError } from 'axios'

import moment from 'moment'

import {
  ButtonContainer,
  ContentContainer,
  Row,
  StyledButton,
  StyledTextButton,
} from './style'
import { DataResultTemplate } from '@/@types/global'
import { tpiSampleTracking as tpiSampleTrackingPost } from '@/api/post'

import { TableWrapper } from '@/pages/Flow/_component/DataToResult/style'
import { useApp } from '@/stores/AppStore'
import { downloadBase64 } from '@/utils/utils'

const DataToResult: React.FC = () => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const {
    setIsLoading,
    templateColumn,
    templateRow,
    setTemplateRow,
    resultItems,
  } = useApp()

  const [initialized, setInitialized] = useState(false)
  const [checkedAll, setCheckedAll] = useState(true)
  const [setItemsPerPage] = useState<number>(50)
  const [resultItemsSelected, setResultItemsSelected] = useState<any[]>([])

  const fillResultChecked = (
    resultItems: any[],
    templateRow: DataResultTemplate[]
  ) => {
    const itemsSelected = templateRow
      .filter((item) => item.checked)
      ?.map((item) => item.index)

    setResultItemsSelected(
      resultItems.filter((item, index) => {
        item.indexResultItems = index
        return itemsSelected.includes(index)
      })
    )
  }

  const addCellRow = (newTemplateRow: any) => {
    newTemplateRow.forEach((element: any, index: number) => {
      element.cell = (
        <Checkbox
          id={`checked-${index}`}
          name={`checked-${index}`}
          checked={!!element.checked}
          label=""
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          onCheckedChange={() => fillItems(index)}
        />
      )
    })
  }

  const fillItems = (index?: number) => {
    if (index === undefined) setCheckedAll(!checkedAll)
    const newTemplateRow = templateRow.map((element, idx) => {
      if (index === undefined) {
        element.checked = !checkedAll
      } else if (idx === index) {
        element.checked = !element.checked
      }
      return element
    })
    addCellRow(newTemplateRow)
    setTemplateRow(newTemplateRow)
    fillResultChecked(resultItems, newTemplateRow)
  }

  const getColumns = (columns: any) => {
    return [
      {
        id: 'cell',
        accessorKey: 'cell',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => (
          <Checkbox
            checked={checkedAll}
            label=""
            onCheckedChange={() => fillItems()}
          />
        ),
        cell: ({ cell }) => cell.getValue(),
      },
      ...columns,
    ]
  }

  useEffect(() => {
    if (templateRow.length > 0 && (!initialized || !templateRow[0].cell)) {
      addCellRow(templateRow)
      setTemplateRow([...templateRow])
      setInitialized(true)
      fillResultChecked(resultItems, templateRow)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, templateRow, setTemplateRow])

  const exportToExcel = async () => {
    try {
      setIsLoading(true)
      const result = await tpiSampleTrackingPost({
        generate: resultItemsSelected,
      })

      downloadBase64(
        document,
        `report_${moment().format('MM-DD-YYYY_hh:mm:ss').toString()}.xlsx`,
        result.file,
        atob(result.file),
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )
    } catch (error) {
      console.log(error)
      if (isAxiosError(error)) {
        toast({
          title: 'Error',
          description: error.response?.data?.errorMessage ?? error.message,
          type: 'error',
        })
      } else {
        toast({
          title: 'Error',
          description: `${error}`,
          type: 'error',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const renderTable = useCallback(() => {
    return templateColumn.length > 0 && templateRow.length > 0 ? (
      <Section.Root
        header={
          <Section.Header
            title=""
            isCollapsable
            actionButtons={
              <ButtonContainer>
                <StyledButton onClick={() => exportToExcel()}>
                  <StyledTextButton>{t<string>('', 'Export')}</StyledTextButton>
                </StyledButton>
              </ButtonContainer>
            }
          />
        }
      >
        <TableWrapper>
          <NewTable.Root
            key="Table-with-result"
            columnIdWithRowspan="button"
            columns={getColumns(templateColumn)}
            data={templateRow}
            isBordered
            hasScroll
            simplePagination={
              <NewTable.Pagination
                key="NewTable-Pagination-results"
                previousPage={() => 0}
                nextPage={() => 0}
                getPageCount={() => 1}
                getCanPreviousPage={() => false}
                getCanNextPage={() => false}
                getState={() => {
                  return {
                    pagination: {
                      pageIndex: 0,
                      pageSize: 1,
                    },
                  }
                }}
                setPageSize={setItemsPerPage}
              />
            }
          />
        </TableWrapper>
      </Section.Root>
    ) : (
      <NewTable.Root key="Table-with-result-no-data" data={[]} columns={[]} />
    )
  }, [templateColumn, templateRow, t])

  return <ContentContainer>{renderTable()}</ContentContainer>
}

export default DataToResult
